import React from 'react'
import { Typography, Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import ResponsiveModalDrawerDialog from 'src/Elements/Utilities/ResponsiveModalDrawerDialog'
import { ActionProps } from 'src/Elements/Utilities/ResponsiveModalDrawerActions'
import { updateAccessRequest } from '../../api'
import { OwnerAccessRequestModel, OwnerAccessRequestUpdateActions } from '../../models'
import { OwnerAccessRequestAccordion, OwnerAccessRequestDialogTable, OwnerAccessRequestIgnoreButton } from '../OwnerAccessRequestDialog'
import { getOwnerAccessRequestQueryKey } from '../../utils'

export interface InstallationAccessRightDialogProps {
  onClose?: () => void
  place?: number
  installationId: number
  accessRequest: OwnerAccessRequestModel
}

/**
 * Dialog component to accept/reject/ignore access request.
 */
const InstallationAccessRightDialog: React.FC<InstallationAccessRightDialogProps> = ({ onClose, installationId, accessRequest, place }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()
  const ownerAccessRequestQueryKey = getOwnerAccessRequestQueryKey(installationId)

  const { id: accessRequestId, createdAt, companyName, reason } = accessRequest

  const handleOnAccept = async (): Promise<void> => {
    if (!installationId) return

    const { hasErrors } = await updateAccessRequest(OwnerAccessRequestUpdateActions.accept, installationId, accessRequestId, authFetch)

    if (hasErrors) {
      enqueueErrorStack(t('dashboard.access-request-dialog.button.accept-request-failure-message'))
      return
    }
    enqueueSuccessStack(t('dashboard.access-request-dialog.button.accept-request-success-message'))

    queryClient.invalidateQueries({ queryKey: ownerAccessRequestQueryKey })
    onClose?.()
  }

  const handleOnReject = async (): Promise<void> => {
    if (!installationId) return

    const { hasErrors } = await updateAccessRequest(OwnerAccessRequestUpdateActions.reject, installationId, accessRequestId, authFetch)

    if (hasErrors) {
      enqueueErrorStack(t('dashboard.access-request-dialog.button.reject-request-failure-message'))
      return
    }
    enqueueSuccessStack(t('dashboard.access-request-dialog.button.reject-request-success-message'))

    queryClient.invalidateQueries({ queryKey: ownerAccessRequestQueryKey })
    onClose?.()
  }

  const handleOnIgnore = (): void => {
    onClose?.()
  }

  const primaryAction: ActionProps = {
    label: t('dashboard.access-request-dialog.button.accept'),
    onClick: handleOnAccept,
  }

  const secondaryAction: ActionProps = {
    label: t('dashboard.access-request-dialog.button.reject'),
    onClick: handleOnReject,
    variant: 'contained',
    color: 'warning',
  }

  const ignoreButton = <OwnerAccessRequestIgnoreButton onClick={handleOnIgnore} />

  return (
    <ResponsiveModalDrawerDialog
      title={t('dashboard.access-request-dialog.title')}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      leftControls={ignoreButton}
      onClose={onClose}
      modalMaxWidth="sm"
    >
      <Stack gap={4}>
        <Typography variant="body2">{t('dashboard.access-request-dialog.text-description')}</Typography>
        <OwnerAccessRequestDialogTable installationId={installationId} accessRequestAt={createdAt} company={companyName} />
        <Typography variant="body1">{reason}</Typography>
        <OwnerAccessRequestAccordion />
      </Stack>
    </ResponsiveModalDrawerDialog>
  )
}

export default InstallationAccessRightDialog
